import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Search, ClipboardList, Heart, User, Bell, MapPin, ChevronDown, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api, inr } from "@/lib/api";
import VSAI from "@/components/customer/VSAI";

export function TopBar({ onSearch }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const addr = user?.addresses?.[0];
  useEffect(() => {
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, []);
  return (
    <div className="sticky top-0 z-40 glass bg-coral/95 text-white pt-3 pb-3 px-4 rounded-b-3xl shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => nav("/addresses")} className="flex items-start gap-1.5 min-w-0 tap" data-testid="location-btn">
          <MapPin size={20} className="shrink-0 mt-0.5" />
          <div className="text-left min-w-0">
            <div className="flex items-center gap-1 font-bold text-sm leading-tight">
              {addr?.label || "Set location"} <ChevronDown size={14} />
            </div>
            <div className="text-[11px] text-white/85 truncate max-w-[46vw]">{addr ? `${addr.line1}` : "Add your delivery address"}</div>
          </div>
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="font-display font-extrabold text-lg tracking-tight mr-1">BiteGoFood</span>
          <button onClick={() => nav("/notifications")} className="relative w-9 h-9 rounded-full bg-white/15 flex items-center justify-center tap" data-testid="notifications-btn">
            <Bell size={18} />
            {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-white text-coral text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{unread}</span>}
          </button>
          <button onClick={() => nav("/profile")} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center tap" data-testid="profile-btn">
            <User size={18} />
          </button>
        </div>
      </div>
      <button onClick={() => (onSearch ? onSearch() : nav("/search"))} className="mt-3 w-full flex items-center gap-2 bg-white text-ink-600 rounded-xl px-3.5 py-2.5 text-sm tap" data-testid="search-bar">
        <Search size={18} className="text-coral" />
        <span className="truncate">Search "biryani", restaurants, dishes…</span>
      </button>
    </div>
  );
}

export function BottomNav() {
  const nav = useNavigate();
  const loc = useLocation();
  const { count } = useCart();
  const items = [
    { to: "/", icon: Home, label: "Home" },
    { to: "/search", icon: Search, label: "Search" },
    { to: "/offers", icon: Tag, label: "Offers" },
    { to: "/orders", icon: ClipboardList, label: "Orders" },
    { to: "/profile", icon: User, label: "Profile" },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 glass bg-white/85 border-t border-border max-w-[520px] mx-auto" data-testid="bottom-nav">
      <div className="grid grid-cols-5">
        {items.map((it) => {
          const active = loc.pathname === it.to;
          return (
            <button key={it.to} onClick={() => nav(it.to)} className="flex flex-col items-center justify-center py-2.5 gap-0.5 tap" data-testid={`nav-${it.label.toLowerCase()}`}>
              <it.icon size={22} className={active ? "text-coral" : "text-ink-600"} strokeWidth={active ? 2.6 : 2} />
              <span className={`text-[10px] font-semibold ${active ? "text-coral" : "text-ink-600"}`}>{it.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export function StickyCart() {
  const { cart, count, subtotal } = useCart();
  const nav = useNavigate();
  const loc = useLocation();
  if (!count || ["/cart", "/checkout"].includes(loc.pathname) || loc.pathname.startsWith("/order/")) return null;
  return (
    <button onClick={() => nav("/cart")} className="fixed bottom-[76px] left-4 right-4 max-w-[488px] mx-auto z-40 bg-ink text-white rounded-2xl px-4 py-3 flex items-center justify-between shadow-lg tap" data-testid="sticky-cart">
      <div className="flex items-center gap-3 min-w-0">
        <img src={cart.restaurant.image} alt="" className="w-9 h-9 rounded-lg object-cover" />
        <div className="text-left min-w-0">
          <div className="text-xs text-white/70 truncate">{cart.restaurant.name}</div>
          <div className="text-sm font-bold">{count} item{count > 1 ? "s" : ""} · {inr(subtotal)}</div>
        </div>
      </div>
      <span className="font-bold text-coral bg-white/95 px-3 py-1.5 rounded-xl text-sm">View Cart</span>
    </button>
  );
}

export default function CustomerLayout({ children, hideNav }) {
  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto relative pb-24">
      {children}
      <StickyCart />
      <VSAI />
      {!hideNav && <BottomNav />}
    </div>
  );
}
